import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { queryKeys } from '@/query-keys'
import { assert } from '@/utils'
import { assertIsDeliverTxSuccess, IBCTransferStatus, broadcastIbcTransaction2 } from '@/wallet-utils'
import { traceIbcStatus2 } from '@/wallet-utils/ibc/trace-ibc-status'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'

export interface BroadcastStakeToWithdrawStTokenToDexMutationReturnType {
  status: IBCTransferStatus
  transaction: DeliverTxResponse
}

const useBroadcastWithdrawStTokenToDexMutation = ({
  withdrawStTokenToDexRaw,
  setWithdrawStTokenToDexTransaction
}: MutationParameters) => {
  const queryClient = useQueryClient()

  const { getSigningStargateClient, getAccount, getOfflineSigner, wallet } = useStrideChain()

  const handleMutation = async (): Promise<BroadcastStakeToWithdrawStTokenToDexMutationReturnType> => {
    assert(withdrawStTokenToDexRaw, 'Unable to broadcast empty signed transaction.')

    assert(wallet, `You are unable to send token without your STRD wallet instance.`)

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = {
      client,
      denom: 'STRD' as const,
      account,
      offlineSigner: getOfflineSigner(),
      wallet: wallet.name
    }
    const tx = await broadcastIbcTransaction2(ibcParams, {
      type: 'default',
      value: withdrawStTokenToDexRaw
    })

    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase })

    assertIsDeliverTxSuccess(tx)

    const status = await traceIbcStatus2({ denom: 'STRD', tx })

    return { status, transaction: tx }
  }

  const handleSuccess = async ({ transaction }: { status: IBCTransferStatus; transaction: DeliverTxResponse }) => {
    flushSync(() => {
      setWithdrawStTokenToDexTransaction(transaction)
    })

    await queryClient.invalidateQueries({
      queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: transaction.transactionHash })
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastWithdrawStTokenToDexMutation }
